import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Users, ShieldCheck, User } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function UsersPage() {
  const { t } = useLanguage();
  const { isAdmin } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${API}/users`, { withCredentials: true });
      setUsers(res.data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateRole = async (userId, newRole) => {
    try {
      await axios.put(`${API}/users/${userId}/role?role=${newRole}`, {}, { withCredentials: true });
      toast.success(t('savedSuccess'));
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errorOccurred'));
    }
  };

  if (!isAdmin) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <p className="text-zinc-500">Admin access required</p>
        </div>
      </Layout>
    );
  }

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="text-zinc-500">{t('loading')}</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div data-testid="users-page">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <Users size={28} weight="fill" className="text-zinc-900" />
          <h2 className="text-xl font-bold" style={{ fontFamily: 'var(--font-heading)' }}>
            {t('users')}
          </h2>
          <span className="px-2 py-1 bg-zinc-100 text-zinc-700 text-xs font-bold">
            {users.length}
          </span>
        </div>

        {/* Users Table - Desktop */}
        <div className="hidden md:block bg-white border border-zinc-200 overflow-x-auto">
          <table className="excel-table" data-testid="users-table">
            <thead>
              <tr>
                <th>{t('name')}</th>
                <th>{t('email')}</th>
                <th>{t('role')}</th>
                <th className="w-40">{t('edit')}</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.user_id} data-testid={`user-row-${user.user_id}`}>
                  <td className="font-medium">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-zinc-200 flex items-center justify-center text-zinc-600 font-semibold text-sm">
                        {user.name?.charAt(0).toUpperCase() || 'U'}
                      </div>
                      {user.name}
                    </div>
                  </td>
                  <td className="text-zinc-500">{user.email}</td>
                  <td>
                    <span className={`px-2 py-1 text-xs font-mono font-bold uppercase ${
                      user.role === 'admin' 
                        ? 'bg-zinc-900 text-white' 
                        : 'bg-zinc-100 text-zinc-700 border border-zinc-200'
                    }`}>
                      {user.role === 'admin' ? (
                        <><ShieldCheck size={12} className="inline mr-1" />{t('admin')}</>
                      ) : (
                        <><User size={12} className="inline mr-1" />{t('staff')}</>
                      )}
                    </span>
                  </td>
                  <td>
                    <Select
                      value={user.role}
                      onValueChange={(val) => updateRole(user.user_id, val)}
                    >
                      <SelectTrigger className="w-32 rounded-none" data-testid={`role-select-${user.user_id}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="admin">{t('admin')}</SelectItem>
                        <SelectItem value="staff">{t('staff')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Users Cards - Mobile */}
        <div className="md:hidden space-y-3">
          {users.map((user) => (
            <div key={user.user_id} className="mobile-card" data-testid={`user-card-${user.user_id}`}>
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-zinc-200 flex items-center justify-center text-zinc-600 font-semibold">
                    {user.name?.charAt(0).toUpperCase() || 'U'}
                  </div>
                  <div>
                    <h3 className="font-medium">{user.name}</h3>
                    <p className="text-sm text-zinc-500">{user.email}</p>
                  </div>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className={`px-2 py-1 text-xs font-mono font-bold uppercase ${
                  user.role === 'admin' 
                    ? 'bg-zinc-900 text-white' 
                    : 'bg-zinc-100 text-zinc-700 border border-zinc-200'
                }`}>
                  {t(user.role)}
                </span>
                <Select
                  value={user.role}
                  onValueChange={(val) => updateRole(user.user_id, val)}
                >
                  <SelectTrigger className="w-32 rounded-none">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">{t('admin')}</SelectItem>
                    <SelectItem value="staff">{t('staff')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          ))}
        </div>
      </div>
    </Layout>
  );
}
